import {
    BaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart';
import {
    STDLblPos,
    STOfPieType,
    STSplitType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    DEFAULT_LABEL_SEPARATOR,
    DataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label';
import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import {
    DEFAULT_CATEGORY,
    DEFAULT_CHILD_BORDER,
    DEFAULT_CHILD_FILL,
    DEFAULT_CHILD_VALUE,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/of-pie-chart.interface';
import i18n from '@i18next/i18n';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import type { ISubChartModel, IOfPieChart } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type {
    IChartShapeProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type {
    IOfPieChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/of-pie-chart.interface';
import type {
    ILegendItem,
    LegendContentType,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import type {
    IChartSeries,
    IDataLabel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { IDataItemProperty } from './data-item/data-item.interface';
import { DataItemProperty } from './data-item';
import { GeometryProperty } from './geometry';
import { IDataLabelProperty } from './data-label/data-label.interface';

export class OfPieChartProperty extends BaseSubChartProperty<IOfPieChart> implements IOfPieChartProperty {
    /** 母图中需要添加的子图的总数项*/
    public parentOtherData: IDataItemProperty;
    /** 子图无数据时的默认项 */
    public childDefaultData: IDataItemProperty;

    public get ofPieType(): STOfPieType {
        return this.uiData.ofPieType ?? STOfPieType.pie;
    }
    public get splitType(): STSplitType {
        return this.uiData.splitType ?? STSplitType.auto;
    }
    public get gapWidth(): number {
        return this.uiData.gapWidth ?? 100;
    }
    public get secondPieSize(): number {
        return this.uiData.secondPieSize ?? 75;
    }
    public get splitPos(): number {
        return this.uiData.splitPos ?? 0;
    }
    public get secondPiePts(): number[] {
        return this.uiData.secondPiePts ?? [];
    }

    public getDataItem(dataId): IDataItemProperty | undefined {
        return [...this.data, this.parentOtherData, this.childDefaultData].find(data => data.id === dataId);
    }

    /* 子母饼图只按类别做图例 */
    public getLegendContentType(): LegendContentType {
        return 'category';
    }

    public processLegendItems(items: ILegendItem[]): ILegendItem[] {
        const firstSeries = this.series?.[0];

        // 子母饼图不按ser只按category，所以这里取第一个ser下的所有data做过滤
        return items
            .filter(dataItem => dataItem.ser.idx === firstSeries?.idx)
            .map((legendItem) => {
                let categoryText = legendItem.dataItem?.category || '';
                const categoryFormatCode = legendItem.dataItem?.categoryFormatCode;
                if (categoryFormatCode) {
                    categoryText = getFormattedNumber(categoryText, categoryFormatCode);
                }

                return {
                    ...legendItem,
                    name: categoryText as string,
                };
            });
    }

    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IDataItemProperty) {
        return new OfPieDataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public generateOtherLabel(
        dataItem: IDataItemProperty,
        tempData: IDataItemProperty,
        extra?: {  tempData: IDataItemProperty, parentTotal: number, childTotal: number },
    ): IDataLabelProperty {
        const [chartSer] = this.series;

        return new OtherDataLabelProperty({
            ...tempData.label,
            ...chartSer.dataLabels,
            ...chartSer.otherDLbl,
        }, this.context).init(dataItem, extra);
    }

    protected initFlattenData() {
        super.initFlattenData();

        // 只取第一个系列的值
        this.data = this.data.filter(dataItem => dataItem.serIndex === 0);
        const [parentOtherData, childDefaultData] = this.genOfPieData();
        this.parentOtherData = parentOtherData;
        this.childDefaultData = childDefaultData;
    }

    /**
     * 生成母图中的子图（子图总和项）和 默认子图项
     * @returns
     */
    private genOfPieData() {
        const [chartSer] = this.series;
        const [tempData] = this.data;

        // 子图有数据时，母图添加一项子图的总数项
        const otherShapeProps = this.getOtherShapeProps(chartSer);
        // @ts-ignore
        const parentOtherDataItem: IDataItemProperty = new DataItemProperty({}, this.context);
        Object.assign(parentOtherDataItem, {
            ...tempData,
            id: DEFAULT_CATEGORY,
            props: otherShapeProps,
            label: this.generateOtherLabel(parentOtherDataItem, tempData),
            geometry: this.generateGeometry(otherShapeProps!, parentOtherDataItem),
            value: undefined,
        });

        // 子图无数据，添加占位项
        // @ts-ignore
        const childDefaultDataItem: IDataItemProperty = new DataItemProperty({}, this.context);
        Object.assign(childDefaultDataItem, {
            ...tempData,
            value: DEFAULT_CHILD_VALUE,
            category: DEFAULT_CATEGORY,
            id: this.getChildPlaceHolderId(),
            props: {
                style: {
                    fillBackground: {
                        solidFill: new SolidFill(DEFAULT_CHILD_FILL),
                    },
                    border: {
                        ...tempData.props?.style.border,
                        color: DEFAULT_CHILD_BORDER,
                    },
                },
            },
            serIndex: 0,
            ser: chartSer,
        });

        return [parentOtherDataItem, childDefaultDataItem];
    }

    private getChildPlaceHolderId(): string {
        return `id_${DEFAULT_CHILD_VALUE}`;
    }

    private getOtherShapeProps(ser: IChartSeries): IChartShapeProps | undefined {
        return ser.otherDPt?.props;
    }

    private generateGeometry(props: IChartShapeProps, dataItem: IDataItemProperty) {
        return new GeometryProperty({ props }, this.context).init(dataItem);
    }
}

class OfPieDataLabelProperty extends DataLabelProperty {
    public get labelPosition(): STDLblPos {
        return this.uiData.labelPosition || STDLblPos.bestFit;
    }
}

class OtherDataLabelProperty extends OfPieDataLabelProperty {
    public init(
        dataItem: IDataItemProperty,
        extra?: { tempData: IDataItemProperty, parentTotal: number, childTotal: number },
    ) {
        this.dataItem = dataItem;
        this.generateLabelContent(dataItem, extra);

        return this;
    }

    protected generateLabelContent(
        dataItem: IDataItemProperty,
        extra?: { tempData: IDataItemProperty, parentTotal: number, childTotal: number},
    ) {
        if (!extra) return super.generateLabelContent(dataItem);
        const { tempData, parentTotal, childTotal } = extra;
        const { label: dataLabel } = tempData;
        if (!dataLabel) {
            this.content = '';
            return;
        }

        const {
            showSeriesName,
            showCategoryName,
            showVal,
            showPercent,
            separator: separator = DEFAULT_LABEL_SEPARATOR,
        } = dataLabel;

        let percent = '';
        let displayVal = '';
        const labelCollect: Array<string> = [];
        if (showPercent) {
            const percentData = childTotal / (childTotal + parentTotal);
            percent = `${Math.round(percentData * 100)}%`;
        }
        if (showVal) {
            displayVal = getFormattedNumber(childTotal, 'General');
        }
        showSeriesName && labelCollect.push(tempData.ser.name);
        showCategoryName && labelCollect.push(i18n.t('其他'));
        showVal && labelCollect.push(displayVal);
        showPercent && labelCollect.push(percent);

        this.content = labelCollect.join(separator);
    }
}
